import { isNotDefined } from '@sgme/fp';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField.tsx';
import { FormSelect } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FormSelect.tsx';
import { CommercialMarginAmountInput } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardCommercialMargin/CommercialMarginAmountInput.tsx';
import { CommercialMarginRateInput } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardCommercialMargin/CommercialMarginRateInput.tsx';
import { Collapsible } from '@/App/tiles/shared/Collapsible';
import { marginTypeChanged } from '@/store/flexi-forward/slice.ts';
import { DEFAULT_MARGIN_TYPE, type MarginType, POSSIBLE_MARGIN_TYPE } from '@/store/flexi-forward/state.ts';
import { useAppDispatch } from '@/store/hooks.ts';

interface FlexiForwardCommercialMarginProps {
  tileId: string;
}

export function FlexiForwardCommercialMargin({ tileId }: FlexiForwardCommercialMarginProps): ReactElement {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const label = formatMessage({ id: 'flexiForwardTile.collapse.commercialMargin.label' });

  function formatSolvingType(value: MarginType): string {
    return formatMessage({ id: `flexiForwardTile.commercialMargin.marginType.option.${value}` });
  }

  const selectedMarginType: MarginType = useFlexiForwardSelector(
    (flexiForward) => flexiForward.properties?.marginType ?? DEFAULT_MARGIN_TYPE,
  );

  const handleMarginTypeChanged = (value: MarginType) => {
    const marginType = POSSIBLE_MARGIN_TYPE.find((marginType) => marginType === value);

    if (isNotDefined(marginType)) {
      return;
    }

    dispatch(marginTypeChanged({ tileId, marginType }));
  };

  return (
    <Collapsible id="commercialMargin" label={label}>
      <FlexiForwardFormField formattedMessageId="flexiForwardTile.mainFields.marginType.label">
        <FormSelect<MarginType>
          values={POSSIBLE_MARGIN_TYPE}
          selectedValue={selectedMarginType}
          onChange={handleMarginTypeChanged}
          formatValue={formatSolvingType}
        />
      </FlexiForwardFormField>

      <FlexiForwardFormField formattedMessageId="flexiForwardTile.mainFields.margin.label">
        {selectedMarginType === 'AMOUNT' ? (
          <CommercialMarginAmountInput tileId={tileId} />
        ) : (
          <CommercialMarginRateInput />
        )}
      </FlexiForwardFormField>
    </Collapsible>
  );
}
